import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Facebook } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../context/BookingContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { IMAGES, CONTACT } from "../data/site";

export const Header = () => {
  const { t } = useLang();
  const { openBooking } = useBooking();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#rooms", label: t.nav.rooms },
    { href: "#gallery", label: t.nav.gallery },
    { href: "#included", label: t.nav.included },
    { href: "#story", label: t.nav.story },
    { href: "#location", label: t.nav.location },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-40 transition-colors duration-500 ${
          scrolled ? "bg-purpleink-deep/90 backdrop-blur-lg shadow-lg" : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
          <a href="#top" className="flex items-center gap-3" data-testid="header-logo">
            <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-white p-0.5 shadow-md">
              <img src={IMAGES.logo} alt="Casa Romano" className="h-full w-full rounded-full object-cover" />
            </span>
            <span className="hidden flex-col leading-none sm:flex">
              <span className="font-display text-lg font-bold tracking-tight text-white">
                Casa Romano
              </span>
              <span className="text-[10px] font-medium uppercase tracking-[0.25em] text-marigold">
                San Juan del Sur
              </span>
            </span>
          </a>

          <nav className="hidden items-center gap-8 lg:flex">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="group relative text-sm font-medium text-white/85 transition-colors hover:text-white"
                data-testid={`nav-${l.href.slice(1)}`}
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-0.5 w-0 bg-marigold transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <a
              href={CONTACT.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-facebook"
              aria-label="Facebook"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-white transition-colors hover:bg-white/10"
            >
              <Facebook className="h-4 w-4" />
            </a>
            <LanguageSwitcher variant="dark" />
            <button
              data-testid="header-book-btn"
              onClick={() => openBooking("")}
              className="hidden rounded-full bg-marigold px-5 py-2.5 text-sm font-semibold text-purpleink-deep shadow-md transition-transform duration-200 hover:bg-marigold-bright active:scale-95 sm:block"
            >
              {t.nav.book}
            </button>
            <button
              className="text-white lg:hidden"
              onClick={() => setMenuOpen(true)}
              data-testid="mobile-menu-open"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-purpleink-deep lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <span className="font-display text-xl font-bold text-white">Casa Romano</span>
              <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu">
                <X className="h-7 w-7 text-white" />
              </button>
            </div>
            <nav className="flex flex-col gap-2 px-6 pt-8">
              {links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMenuOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i }}
                  className="border-b border-white/10 py-4 font-display text-3xl text-white"
                  data-testid={`mobile-nav-${l.href.slice(1)}`}
                >
                  {l.label}
                </motion.a>
              ))}
              <button
                onClick={() => {
                  setMenuOpen(false);
                  openBooking("");
                }}
                className="mt-8 rounded-full bg-marigold py-4 text-lg font-semibold text-purpleink-deep"
                data-testid="mobile-book-btn"
              >
                {t.nav.book}
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
